<script setup lang="ts">
import type { CommonCurrencySelectorProperties, Currency } from '@optionway/models';

const properties = defineProps<CommonCurrencySelectorProperties>();

const prefix = 'components.common.currencySelector';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.commonCurrencySelector;

const componentConfig = computed<CommonCurrencySelectorProperties>(() => {
  return {
    currencies: properties.currencies ?? config?.currencies,
  };
});

const currencyStore = useCurrencyStore();
const gtmStore = useGtmStore();
const { currency, currencyList } = storeToRefs(currencyStore);

// Client-side persistence of the chosen currency: the store state stays serializable,
// the persisted preference is applied after hydration so SSR and first client render agree.
// No storage entry yet → fall back to the store's value: the app default lives there alone.
const persistedCurrency = useLocalStorage<Currency>('currency', currency.value);

const isOpen = ref(false);

const allowedCurrenciesNotSelected = computed(() =>
  currencyList.value.filter((c) => c !== currency.value && componentConfig.value.currencies?.includes(c)),
);

const allowedCurrencies = computed(() =>
  currencyList.value.filter((c) => componentConfig.value.currencies?.includes(c)),
);

watch(currency, (value) => {
  persistedCurrency.value = value;
});

// The user's choice, as opposed to the persisted preference replayed on mount, which is not an event.
const changeCurrency = (current: Currency) => {
  gtmStore.trackCurrencyChanged(currency.value, current);
  currencyStore.changeCurrency(current);
};

const selectCurrency = (current: Currency) => {
  changeCurrency(current);
  isOpen.value = false;
};

onMounted(() => {
  // Validated against the partner-allowed list, not just CurrencyEnum: a stale persisted value
  // outside the configured set would display a currency absent from the selectable sheet.
  if (componentConfig.value.currencies?.includes(persistedCurrency.value)) {
    currencyStore.changeCurrency(persistedCurrency.value);
    return;
  }
  persistedCurrency.value = currency.value;
});

if (import.meta.test) {
  defineExpose({ allowedCurrenciesNotSelected });
}
</script>

<template>
  <!-- Mounted from day one, rendered only when the partner offers a real choice: with a single
       allowed currency there is nothing to select (first prod ships single-currency) — the
       persistence machinery in the script stays live either way. -->
  <template v-if="allowedCurrencies.length > 1">
    <!-- Desktop: NavigationMenu dropdown -->
    <div class="hidden lg:block">
      <UiNavigationMenu v-if="allowedCurrenciesNotSelected.length > 0">
        <UiNavigationMenuList>
          <UiNavigationMenuItem>
            <UiNavigationMenuTrigger :aria-label="$t(`${prefix}.label`)">
              <div class="flex flex-row items-center gap-2.5 px-3 py-2.5 lg:p-0">
                <span class="px-0.5 text-secondary-foreground">{{ currency }}</span>
              </div>
            </UiNavigationMenuTrigger>
            <UiNavigationMenuContent>
              <div class="w-32 p-1">
                <template
                  v-for="current in allowedCurrenciesNotSelected"
                  :key="current"
                >
                  <UiButton
                    variant="tertiary"
                    color="gray"
                    class="flex h-10 w-full cursor-pointer items-center justify-between"
                    @click="changeCurrency(current)"
                  >
                    <span>{{ current }}</span>
                  </UiButton>
                </template>
              </div>
            </UiNavigationMenuContent>
          </UiNavigationMenuItem>
        </UiNavigationMenuList>
      </UiNavigationMenu>
      <span
        v-else
        class="px-3.5 py-2.5 text-secondary-foreground"
        >{{ currency }}</span
      >
    </div>

    <!-- Mobile: Row button in burger menu -->
    <UiSheet v-model:open="isOpen">
      <UiSheetTrigger
        v-if="allowedCurrenciesNotSelected.length > 0"
        as-child
      >
        <UiButton
          variant="tertiary"
          color="gray"
          class="flex w-full flex-row items-center justify-between gap-2.5 px-3 py-2.5 text-base font-semibold lg:hidden"
        >
          <span>{{ $t(`${prefix}.label`) }}</span>
          <div class="flex flex-row items-center gap-1">
            <span class="px-0.5">{{ currency }}</span>
            <SvgoCommonArrowRight
              filled
              aria-hidden="true"
            />
          </div>
        </UiButton>
      </UiSheetTrigger>
      <div
        v-else
        class="flex justify-between px-3 py-2.5 text-base font-semibold text-secondary-foreground lg:hidden"
      >
        <span>{{ $t(`${prefix}.label`) }}</span>
        <span>{{ currency }}</span>
      </div>
      <UiSheetContent
        side="right"
        class="w-full p-0 lg:w-80"
      >
        <!-- Mobile: Full-screen sub-page overlay -->
        <div class="inset-0 bg-white lg:hidden">
          <!-- Sub-page header -->
          <div class="flex items-center justify-between px-4 py-4">
            <span class="flex-1 text-center font-medium text-secondary-foreground">
              {{ $t(`${prefix}.label`) }}
            </span>
            <!-- Spacer to balance the back button -->
            <div class="w-10" />
          </div>

          <!-- Currency list -->
          <div class="flex flex-col gap-1 px-2">
            <template
              v-for="current in allowedCurrencies"
              :key="current"
            >
              <UiButton
                variant="tertiary"
                color="gray"
                :aria-current="current === currency ? 'true' : undefined"
                class="flex items-center justify-between rounded-lg px-4 py-3"
                :class="{
                  'border border-brand-primary-700': current === currency,
                }"
                @click="selectCurrency(current)"
              >
                <span>{{ current }}</span>
                <SvgoCommonLocaleSelectorCheck
                  v-if="current === currency"
                  filled
                  aria-hidden="true"
                />
              </UiButton>
            </template>
          </div>
        </div>
      </UiSheetContent>
    </UiSheet>
  </template>
</template>
